import { defineStore } from 'pinia';
import { ref, type Component } from 'vue';
import { __ } from './expressions';

export type ConfirmationDialogParams = {
    title?: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    confirmIcon?: Component;
    cancelIcon?: Component;
    callback?: () => void;
};

export const useConfirmationDialog = defineStore('confirmationDialog', () => {
    const defaultParams: ConfirmationDialogParams = {
        title: '',
        message: '',
        confirmText: __('confirm'),
        cancelText: __('cancel'),
    };

    const open = ref<boolean>(false);
    const loading = ref<boolean>(false);

    const dialogConfig = ref<ConfirmationDialogParams>(defaultParams);

    const resolver = ref<((value: boolean) => void) | null>(null);

    function openDialog(dialogParams: ConfirmationDialogParams): Promise<boolean> {
        dialogConfig.value = dialogParams;
        open.value = true;
        loading.value = false;
        return new Promise<boolean>((resolve) => {
            resolver.value = resolve;
        });
    }

    async function confirm() {
        if (dialogConfig.value.callback) {
            loading.value = true;
            try {
                await dialogConfig.value.callback();
            } catch (error) {
                console.error('Error in confirmation callback:', error);
            }
        }
        loading.value = false;
        resolver.value?.(true);
        reset();
    }

    function cancel() {
        resolver.value?.(false);
        reset();
    }

    function reset() {
        open.value = false;
        loading.value = false;
        dialogConfig.value = { ...defaultParams };
        resolver.value = null;
    }

    return {
        open,
        loading,
        dialogConfig,
        openDialog,
        cancel,
        confirm,
    };
});
